import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { useForm } from 'react-hook-form';

import { Button } from '../../shared/ui/Button';
import { Dialog, DialogBody, DialogFooter, DialogHeader } from '../../shared/ui/Dialog';
import { FormError } from '../../shared/ui/FormError';
import { Input } from '../../shared/ui/Input';
import { Label } from '../../shared/ui/Label';
import { Select } from '../../shared/ui/Select';
import { StatusBanner } from '../../shared/ui/StatusBanner';

import s from './AddStudentDialog.module.css';
import { INSTITUTE_QUERY_KEYS, inviteStudent, provisionStudent } from './api';
import { CredentialCard, type StudentCredential } from './CredentialCard';
import {
  addStudentSchema,
  type AddStudentFormInput,
  type AddStudentMode,
  EXAM_TYPES,
} from './schemas';

interface Props {
  instituteId: string;
  open: boolean;
  onClose: () => void;
}

const EMPTY: AddStudentFormInput = {
  mode: 'credential',
  name: '',
  email: '',
  username: '',
  phone: '',
  roll_no: '',
  exam_type: 'academic',
  target_band: '',
};

export function AddStudentDialog({ instituteId, open, onClose }: Props): JSX.Element {
  const titleId = useId();
  const queryClient = useQueryClient();
  const [serverError, setServerError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [credential, setCredential] = useState<StudentCredential | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<AddStudentFormInput>({
    resolver: zodResolver(addStudentSchema),
    defaultValues: EMPTY,
  });
  const mode = watch('mode');

  function invalidate(): void {
    void queryClient.invalidateQueries({ queryKey: INSTITUTE_QUERY_KEYS.students(instituteId) });
    void queryClient.invalidateQueries({
      queryKey: INSTITUTE_QUERY_KEYS.stats.studentsCount(instituteId),
    });
  }

  const mutation = useMutation({
    mutationFn: async (values: AddStudentFormInput) => {
      const rollNo = values.roll_no === '' ? null : values.roll_no;
      const targetBand = values.target_band === '' ? null : parseFloat(values.target_band);
      if (values.mode === 'credential') {
        const result = await provisionStudent({
          institute_id: instituteId,
          username: values.username,
          name: values.name,
          phone: values.phone === '' ? null : values.phone,
          roll_no: rollNo,
          exam_type: values.exam_type,
          target_band: targetBand,
        });
        return { kind: 'credential' as const, result, name: values.name };
      }
      const result = await inviteStudent({
        institute_id: instituteId,
        email: values.email,
        name: values.name,
        phone: values.phone,
        roll_no: rollNo,
        exam_type: values.exam_type,
        target_band: targetBand,
      });
      return { kind: 'email' as const, result };
    },
    onSuccess: (payload) => {
      if (payload.kind === 'credential') {
        if (payload.result.ok && payload.result.password !== null) {
          setCredential({
            name: payload.name,
            username: payload.result.username,
            password: payload.result.password,
            loginUrl: window.location.origin + payload.result.login_path,
          });
          reset({ ...EMPTY, mode: 'credential' });
          invalidate();
        } else {
          setServerError(payload.result.error ?? 'Could not create login');
        }
      } else if (payload.result.ok) {
        setSuccessMsg(
          payload.result.invite_sent
            ? `Invite emailed to ${payload.result.email}.`
            : `${payload.result.email} already had an account — added to your roster.`,
        );
        reset({ ...EMPTY, mode: 'email' });
        invalidate();
      } else {
        setServerError(payload.result.error ?? 'Invite failed');
      }
    },
    onError: (err: Error) => setServerError(err.message),
  });

  async function onSubmit(values: AddStudentFormInput): Promise<void> {
    setServerError(null);
    setSuccessMsg(null);
    try {
      await mutation.mutateAsync(values);
    } catch {
      /* onError set the message */
    }
  }

  function switchMode(next: AddStudentMode): void {
    if (next === mode) return;
    setServerError(null);
    setSuccessMsg(null);
    setValue('mode', next, { shouldValidate: false });
  }

  function handleClose(): void {
    setServerError(null);
    setSuccessMsg(null);
    setCredential(null);
    reset(EMPTY);
    onClose();
  }

  function addAnother(): void {
    setCredential(null);
    setServerError(null);
    reset({ ...EMPTY, mode: 'credential' });
  }

  const busy = isSubmitting || mutation.isPending;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      labelledById={titleId}
      size="md"
      mobilePresentation="sheet"
    >
      {credential !== null ? (
        <>
          <DialogHeader
            titleId={titleId}
            title="Student login created"
            subtitle={`Share these sign-in details with ${credential.name}.`}
            onClose={handleClose}
          />
          <DialogBody>
            <CredentialCard credential={credential} />
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={addAnother}>
              Add another
            </Button>
            <Button type="button" onClick={handleClose}>
              Done
            </Button>
          </DialogFooter>
        </>
      ) : (
        <>
          <DialogHeader
            titleId={titleId}
            title="Add a student"
            subtitle={
              mode === 'credential'
                ? 'Create a username + password login they can use right away — no email needed.'
                : 'Send an email magic-link so they set their own password.'
            }
            onClose={handleClose}
          />
          <form
            noValidate
            onSubmit={(event) => {
              void handleSubmit(onSubmit)(event);
            }}
          >
            <DialogBody>
              <div className={s.modeToggle} role="group" aria-label="How the student signs in">
                <button
                  type="button"
                  className={`${s.modeBtn ?? ''} ${mode === 'credential' ? (s.modeBtnActive ?? '') : ''}`.trim()}
                  aria-pressed={mode === 'credential'}
                  onClick={() => switchMode('credential')}
                >
                  Create login
                </button>
                <button
                  type="button"
                  className={`${s.modeBtn ?? ''} ${mode === 'email' ? (s.modeBtnActive ?? '') : ''}`.trim()}
                  aria-pressed={mode === 'email'}
                  onClick={() => switchMode('email')}
                >
                  Email invite
                </button>
              </div>

              <div className={s.grid}>
                <div className={s.full}>
                  <Label htmlFor="add_name">Full name</Label>
                  <Input
                    id="add_name"
                    autoComplete="off"
                    placeholder="Anisha Nepal"
                    invalid={errors.name !== undefined}
                    aria-describedby={errors.name ? 'add_name-error' : undefined}
                    {...register('name')}
                  />
                  <FormError id="add_name-error" message={errors.name?.message} />
                </div>

                {mode === 'credential' ? (
                  <>
                    <div>
                      <Label htmlFor="add_username">Username</Label>
                      <Input
                        id="add_username"
                        autoComplete="off"
                        autoCapitalize="none"
                        spellCheck={false}
                        placeholder="anisha.n or roll no"
                        invalid={errors.username !== undefined}
                        aria-describedby={
                          errors.username ? 'add_username-error' : 'add_username-hint'
                        }
                        {...register('username')}
                      />
                      <FormError id="add_username-error" message={errors.username?.message} />
                      {errors.username === undefined ? (
                        <p id="add_username-hint" className={s.hint}>
                          Letters, numbers, dot, dash, underscore. Shown on the login sheet.
                        </p>
                      ) : null}
                    </div>
                    <div>
                      <Label htmlFor="add_phone">Phone (optional)</Label>
                      <Input
                        id="add_phone"
                        type="tel"
                        autoComplete="off"
                        placeholder="+9779812345678"
                        invalid={errors.phone !== undefined}
                        aria-describedby={errors.phone ? 'add_phone-error' : undefined}
                        {...register('phone')}
                      />
                      <FormError id="add_phone-error" message={errors.phone?.message} />
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <Label htmlFor="add_email">Email</Label>
                      <Input
                        id="add_email"
                        type="email"
                        inputMode="email"
                        autoComplete="off"
                        placeholder="student@example.com"
                        invalid={errors.email !== undefined}
                        aria-describedby={errors.email ? 'add_email-error' : undefined}
                        {...register('email')}
                      />
                      <FormError id="add_email-error" message={errors.email?.message} />
                    </div>
                    <div>
                      <Label htmlFor="add_phone">Phone (E.164)</Label>
                      <Input
                        id="add_phone"
                        type="tel"
                        autoComplete="off"
                        placeholder="+9779812345678"
                        invalid={errors.phone !== undefined}
                        aria-describedby={errors.phone ? 'add_phone-error' : undefined}
                        {...register('phone')}
                      />
                      <FormError id="add_phone-error" message={errors.phone?.message} />
                    </div>
                  </>
                )}

                <div>
                  <Label htmlFor="add_roll">Roll # (optional)</Label>
                  <Input id="add_roll" autoComplete="off" {...register('roll_no')} />
                  <FormError id="add_roll-error" message={errors.roll_no?.message} />
                </div>
                <div>
                  <Label htmlFor="add_target">Target band</Label>
                  <Input
                    id="add_target"
                    inputMode="decimal"
                    placeholder="7.5"
                    invalid={errors.target_band !== undefined}
                    aria-describedby={errors.target_band ? 'add_target-error' : undefined}
                    {...register('target_band')}
                  />
                  <FormError id="add_target-error" message={errors.target_band?.message} />
                </div>
                <div className={s.full}>
                  <Label htmlFor="add_examtype">Exam type</Label>
                  <Select id="add_examtype" {...register('exam_type')}>
                    {EXAM_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>

              {serverError !== null ? (
                <div className={s.banner}>
                  <StatusBanner tone="error">{serverError}</StatusBanner>
                </div>
              ) : null}
              {successMsg !== null ? (
                <div className={s.banner}>
                  <StatusBanner tone="success">{successMsg}</StatusBanner>
                </div>
              ) : null}
            </DialogBody>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={handleClose}>
                Close
              </Button>
              <Button type="submit" loading={busy}>
                {mode === 'credential' ? 'Create login' : 'Send invite'}
              </Button>
            </DialogFooter>
          </form>
        </>
      )}
    </Dialog>
  );
}
